import {tokens, Stack} from '@pleo-io/telescope'
import {graphql, PageProps} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Footer from '@/components/footer'
import Header from '@/components/header'
import DemoForm from '@/components/lead-form/demo-form'
import SEO from '@/components/seo'
import {DemoPageQuery} from '@/graphql-types'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Image from '@helios/shared/components/image'
import {Layout, container, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<DemoPageQuery>

const DemoPage: React.FC<Props> = (props) => {
    const data = props.data.allPrismicDemoPage.nodes[0].data
    const formData = props.data.allPrismicBigform.nodes[0].data

    if (!data || !formData) {
        return handleNoPrismicData()
    }

    return (
        <BackgroundWrapper color="muted">
            <SEO
                title={data.meta_title}
                description={data.meta_description}
                openGraphImageUrl={data.open_graph_image?.url}
            />

            <HeaderWrapper>
                <Header hideNav />
            </HeaderWrapper>

            <MainSection variant="two-asymmetrical">
                <Content space={40}>
                    <Text size="lg">{data.title}</Text>

                    <Stack as="ul" space={20}>
                        {data.bullet_points.map((bullet, i) => (
                            <BulletPoint key={`${bullet.text}-${i}`}>
                                <Icon image={bullet.icon} width={50} />
                                <Text>{bullet.text}</Text>
                            </BulletPoint>
                        ))}
                    </Stack>
                </Content>

                <DemoForm
                    content={{
                        title: data.form_title,
                        data: {
                            ...formData,
                            demo_submit_text: formData.submit_text,
                        },
                    }}
                    hubspotProperties={{
                        conversion_description: 'big-form',
                        lifecyclestage: 'lead',
                    }}
                />
            </MainSection>

            {data.social_proof_logos_section && (
                <TitleWithIconGridSection
                    title={data.social_proof_logos_section.document.data.title}
                    items={data.social_proof_logos_section.document.data.logos.map((item) => ({
                        icon: item.logo,
                    }))}
                />
            )}

            <Footer />
        </BackgroundWrapper>
    )
}

const HeaderWrapper = styled.div`
    header {
        ${mq.min('xs')} {
            background: transparent;
        }
    }
`

const MainSection = styled(Layout)`
    display: grid;
    align-items: center;

    ${mq.min('xs')} {
        ${container}
    }

    ${mq.max('md')} {
        grid-template-columns: 1fr;
    }

    > :first-child {
        ${mq.max('md')} {
            grid-row: 2;
        }
    }
`

const Content = styled(Stack)`
    padding-top: ${tokens.spacing40};
    padding-bottom: ${tokens.spacing40};

    ${mq.max('xs')} {
        ${container}
    }
`

const BulletPoint = styled.li`
    display: grid;
    column-gap: ${tokens.spacing20};
    grid-auto-flow: column;
    justify-content: start;
    align-items: center;
`

const Icon = styled(Image)`
    width: 50px;
`

export const query = graphql`
    query DemoPage($uid: String!, $locale: String!) {
        allPrismicBigform(filter: {lang: {eq: $locale}}) {
            nodes {
                ...Bigform
            }
        }
        allPrismicDemoPage(filter: {uid: {eq: $uid}, lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    bullet_points {
                        icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        text
                    }
                    form_title
                    social_proof_logos_section {
                        document {
                            ... on PrismicSocialProofLogosSection {
                                data {
                                    title
                                    logos {
                                        logo {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(DemoPage)
